const ONESIGNAL_API_KEY = process.env.REACT_APP_ONESIGNAL_API_KEY;
const ONESIGNAL_APP_ID = process.env.REACT_APP_ONESIGNAL_APP_ID;

// Fonction pour obtenir le nombre d'utilisateurs abonnés avec plusieurs requêtes
export const getSubscribedUsersCount = async () => {
  try {
    const limit = 200; // Limite par page
    let allPlayers = [];

    // Première requête pour obtenir le nombre total d'utilisateurs
    const initialResponse = await fetch(
      `https://onesignal.com/api/v1/players?app_id=${ONESIGNAL_APP_ID}&limit=1&offset=0`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Basic ${ONESIGNAL_API_KEY}`,
          'Accept': 'application/json'
        }
      }
    );

    const initialData = await initialResponse.json();
    
    const totalCount = initialData.total_count || 0;

    // Calculer le nombre de pages nécessaires
    const totalPages = Math.ceil(totalCount / limit);

    // Récupérer toutes les pages
    let currentOffset = 0;
    while (currentOffset < totalCount) {
      
      const response = await fetch(
        `https://onesignal.com/api/v1/players?app_id=${ONESIGNAL_APP_ID}&limit=${limit}&offset=${currentOffset}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Basic ${ONESIGNAL_API_KEY}`,
            'Accept': 'application/json'
          }
        }
      );

      const data = await response.json();

      if (data.players && data.players.length > 0) {
        allPlayers = [...allPlayers, ...data.players];
      }

      currentOffset += limit;

      // Petit délai entre chaque requête pour éviter les limitations
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    // Analyser les données des utilisateurs
    const userAnalysis = allPlayers.reduce((acc, player) => {
      // Compter les types d'appareils
      acc.deviceTypes[player.device_type] = (acc.deviceTypes[player.device_type] || 0) + 1;
      
      // Compter les systèmes d'exploitation
      acc.deviceOS[player.device_os] = (acc.deviceOS[player.device_os] || 0) + 1;
      
      // Compter les modèles d'appareils
      acc.deviceModels[player.device_model] = (acc.deviceModels[player.device_model] || 0) + 1;
      
      // Analyser les identifiants et compter les abonnés/désabonnés
      if (player.invalid_identifier) {
        // Appareil vraiment inactif (jamais activé ou désinstallé il y a longtemps)
        acc.invalidIdentifiers++;
        acc.subscriptionStatus.invalid++;
      } else if (!player.identifier) {
        // Désabonné mais pas invalidé
        acc.unsubscribed++;
        acc.subscriptionStatus.unsubscribed++;
      } else if (player.notification_types <= 0) {
        // A un identifiant mais notifications désactivées
        acc.notificationsDisabled++;
        acc.subscriptionStatus.notificationsDisabled++;
      } else {
        // Appareil actif avec notifications activées
        acc.validIdentifiers++;
        acc.subscribed++;
        acc.subscriptionStatus.active++;
      }
      
      return acc;
    }, {
      deviceTypes: {},
      deviceOS: {},
      deviceModels: {},
      validIdentifiers: 0,
      invalidIdentifiers: 0,
      subscribed: 0,
      unsubscribed: 0,
      notificationsDisabled: 0,
      subscriptionStatus: {
        active: 0,
        unsubscribed: 0,
        invalid: 0,
        notificationsDisabled: 0
      }
    });

    return {
      total: allPlayers.length,
      subscribed: userAnalysis.subscribed,
      unsubscribed: userAnalysis.unsubscribed,
      notificationsDisabled: userAnalysis.notificationsDisabled,
      neverSubscribed: userAnalysis.neverSubscribed,
      blocked: userAnalysis.blocked,
      validIdentifiers: userAnalysis.validIdentifiers,
      invalidIdentifiers: userAnalysis.invalidIdentifiers,
      deviceTypes: userAnalysis.deviceTypes,
      deviceOS: userAnalysis.deviceOS,
      deviceModels: userAnalysis.deviceModels,
      subscriptionStatus: userAnalysis.subscriptionStatus,
      players: allPlayers
    };
  } catch (error) {
    console.error('Erreur lors de la récupération des utilisateurs OneSignal:', error);
    throw error;
  }
};

// Fonction pour obtenir les statistiques d'une notification
export const getNotificationStats = async (notificationId) => {
  try {
    const response = await fetch(
      `https://onesignal.com/api/v1/notifications/${notificationId}?app_id=${ONESIGNAL_APP_ID}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Basic ${ONESIGNAL_API_KEY}`,
          'Accept': 'application/json'
        }
      }
    );

    const data = await response.json();
    
    return {
      recipients: data.recipients,
      delivered: data.delivered,
      opened: data.opened,
      failed: data.failed,
      clicked: data.clicked
    };
  } catch (error) {
    console.error('Erreur lors de la récupération des statistiques de notification:', error);
    throw error;
  }
};

// Fonction pour obtenir le statut d'abonnement d'un utilisateur
export const getUserSubscriptionStatus = async (playerId) => {
  try {
    const response = await fetch(
      `https://onesignal.com/api/v1/players/${playerId}?app_id=${ONESIGNAL_APP_ID}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Basic ${ONESIGNAL_API_KEY}`,
          'Accept': 'application/json'
        }
      }
    );

    const data = await response.json();
    
    return {
      enabled: data.enabled,
      notificationTypes: data.notification_types,
      status: data.enabled && data.notification_types > 0 ? 'active' : 
              data.notification_types === 0 ? 'never_subscribed' : 
              data.notification_types === -99 ? 'blocked' : 'unsubscribed'
    };
  } catch (error) {
    console.error('Erreur lors de la récupération du statut d\'abonnement:', error);
    throw error;
  }
};

// Fonction pour obtenir les statistiques globales
export const getGlobalStats = async () => {
  try {
    // Récupérer les utilisateurs avec pagination
    const usersStats = await getSubscribedUsersCount();
    
    // Récupérer les notifications
    const notificationsResponse = await fetch(
      `https://onesignal.com/api/v1/notifications?app_id=${ONESIGNAL_APP_ID}&limit=50`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Basic ${ONESIGNAL_API_KEY}`,
          'Accept': 'application/json'
        }
      }
    );

    const notificationsData = await notificationsResponse.json();

    // Calculer les statistiques globales des notifications
    const globalNotificationStats = notificationsData.notifications.reduce((acc, notification) => {
      acc.totalRecipients += notification.recipients || 0;
      acc.totalDelivered += notification.delivered || 0;
      acc.totalOpened += notification.opened || 0;
      acc.totalFailed += notification.failed || 0;
      acc.totalClicked += notification.clicked || 0;
      return acc;
    }, {
      totalRecipients: 0,
      totalDelivered: 0,
      totalOpened: 0,
      totalFailed: 0,
      totalClicked: 0
    });

    return {
      users: usersStats,
      notifications: globalNotificationStats,
      lastNotifications: notificationsData.notifications.slice(0, 5)
    };
  } catch (error) {
    console.error('Erreur lors de la récupération des statistiques globales:', error);
    throw error;
  }
}; 